import { TriangleAlertIcon } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { ProductDetailDTO } from "@/modules/products/domain/types";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";

export type ProductWithoutLotRow = {
  variant: ProductDetailDTO["variants"][number];
  withoutLotQuantity: number;
};

type ProductStockWithoutLotAlertProps = {
  rows: ProductWithoutLotRow[];
  isLoading?: boolean;
  canManage?: boolean;
  onManage: (variantId: string) => void;
  className?: string;
};

export function ProductStockWithoutLotAlert({
  rows,
  isLoading,
  canManage,
  onManage,
  className,
}: ProductStockWithoutLotAlertProps) {
  if (isLoading) {
    return <Skeleton className={cn("h-24 rounded-2xl", className)} />;
  }

  if (rows.length === 0) return null;

  const totalQty = rows.reduce((sum, row) => sum + row.withoutLotQuantity, 0);

  return (
    <div
      className={cn(
        "brand-card flex flex-col gap-3 border-destructive/25 bg-destructive/5 p-4",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-destructive/15 text-destructive">
          <TriangleAlertIcon />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">
            ມີ {rows.length} ຕົວເລືອກທີ່ບໍ່ມີ lot (
            {totalQty.toLocaleString("lo-LA")} ຊິ້ນລວມ)
          </p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            COGS ອາດບໍ່ຖືກ — ກົດລາຍການເພື່ອຈັດເຂົ້າ lot ຫຼື ປັບສະຕັອກ
          </p>
        </div>
      </div>

      <ul className="flex flex-col gap-1.5 text-xs">
        {rows.map(({ variant, withoutLotQuantity }) => {
          const content = (
            <>
              <span className="truncate font-medium">{variant.name}</span>
              <span className="shrink-0 text-destructive tabular-nums">
                {formatQtyWithUnit(withoutLotQuantity, variant.stockUnitLabel)}{" "}
                ບໍ່ມີ lot
              </span>
            </>
          );

          if (!canManage) {
            return (
              <li
                key={variant.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/15 bg-background/60 px-3 py-2"
              >
                {content}
              </li>
            );
          }

          return (
            <li key={variant.id}>
              <button
                type="button"
                onClick={() => onManage(variant.id)}
                className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/15 bg-background/60 px-3 py-2 text-left transition-colors hover:border-destructive/35 hover:bg-background"
              >
                {content}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
